import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Sparkles, Volume2, VolumeX } from 'lucide-react';
import { useVoiceCopilot } from '@/contexts/VoiceCopilotContext';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTranslation } from 'react-i18next';

const VoiceCommandBar: React.FC = () => {
  const { t } = useTranslation();
  const {
    isListening,
    isProcessing,
    isSpeaking,
    isMuted,
    isSupported,
    transcript,
    startListening,
    stopListening,
    toggleMute,
    stopSpeaking,
  } = useVoiceCopilot();

  const pressStartTimeRef = useRef<number>(0);
  const wasListeningOnDownRef = useRef<boolean>(false);

  const handlePointerDown = () => {
    if (!isSupported || isProcessing) return;
    if (isSpeaking) {
      stopSpeaking();
      return;
    }
    pressStartTimeRef.current = Date.now();
    wasListeningOnDownRef.current = isListening;
    if (!isListening) {
      startListening();
    }
  };

  const handlePointerUp = () => {
    if (!isSupported || isProcessing) return;
    const duration = Date.now() - pressStartTimeRef.current;
    if (duration > 250) {
      if (isListening) {
        stopListening();
      }
    } else {
      if (wasListeningOnDownRef.current && isListening) {
        stopListening();
      }
    }
  };

  if (!isSupported) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              disabled
              className="h-9 px-2.5 text-muted-foreground/40"
            >
              <MicOff className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">
              {t('copilot.notSupported', 'El reconocimiento de voz no está disponible en este navegador.')}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-center gap-1.5">
      {/* Live transcript pill shown only while listening & when speaking */}
      {isListening && (
        <div className="absolute right-full mr-3 hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-full bg-purple-600/90 backdrop-blur border border-purple-500/50 text-white text-sm font-medium animate-in fade-in-50 whitespace-nowrap shadow-lg">
          <Sparkles className="h-4 w-4 text-purple-200 animate-spin" />
          <span>{transcript.trim().length > 0 ? `"${transcript}"` : t('copilot.listeningHint', 'Escuchando... (Suelta para enviar)')}</span>
        </div>
      )}

      {/* Speaking voice bubble */}
      {isSpeaking && (
        <div className="absolute right-full mr-3 hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600/95 backdrop-blur border border-emerald-400 text-white text-sm font-semibold animate-in fade-in-50 whitespace-nowrap shadow-xl">
          <Volume2 className="h-4 w-4 animate-bounce" />
          <span>BASS Line respondiendo...</span>
          <button
            onClick={stopSpeaking}
            className="ml-1 text-xs underline opacity-80 hover:opacity-100"
          >
            Silenciar
          </button>
        </div>
      )}

      {/* Main Trigger Button */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="relative group flex flex-col items-center">
              {/* Ripple Effect Background */}
              {!isListening && !isProcessing && (
                <span className="absolute top-0 w-20 h-20 rounded-full border-2 border-fuchsia-400 animate-ping opacity-20 pointer-events-none group-hover:opacity-40" />
              )}
              <Button
                variant="default"
                size="icon"
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onContextMenu={(e) => e.preventDefault()}
                style={{ touchAction: 'none', userSelect: 'none' }}
                disabled={isProcessing}
                className={`relative h-20 w-20 flex items-center justify-center transition-all duration-300 rounded-full select-none shadow-xl border-[3px] overflow-hidden ${
                  isListening
                    ? 'bg-purple-600 border-purple-400 hover:bg-purple-700 text-white shadow-purple-500/50 animate-pulse scale-110'
                    : isProcessing
                    ? 'bg-purple-500/80 border-purple-300 text-white shadow-purple-500/40 animate-pulse'
                    : isSpeaking
                    ? 'bg-emerald-600 border-emerald-400 text-white shadow-emerald-500/50 animate-pulse'
                    : 'bg-gradient-to-tr from-purple-600 via-fuchsia-500 to-pink-500 border-background text-white hover:scale-105 shadow-pink-500/40'
                }`}
              >
                {/* Shine effect overlay for standard state */}
                {!isListening && !isProcessing && !isSpeaking && (
                  <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/20 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                )}
                
                {isListening ? (
                  <Mic className="h-9 w-9 animate-bounce text-white relative z-10" />
                ) : isProcessing ? (
                  <Sparkles className="h-9 w-9 animate-spin text-white relative z-10" />
                ) : isSpeaking ? (
                  <Volume2 className="h-9 w-9 animate-pulse text-white relative z-10" />
                ) : (
                  <Mic className="h-9 w-9 text-white drop-shadow-md" />
                )}
              </Button>

              {/* Text Pill below the button */}
              <div className="flex items-center gap-1 mt-2">
                <span
                  className={cn(
                    'text-xs font-bold tracking-tight px-3 py-1.5 rounded-full shadow-sm select-none',
                    isListening
                      ? 'bg-purple-600 text-white animate-pulse'
                      : isProcessing
                      ? 'bg-purple-500/20 text-purple-600 dark:text-purple-400'
                      : isSpeaking
                      ? 'bg-emerald-600 text-white animate-pulse'
                      : 'text-foreground/90 bg-background border'
                  )}
                >
                  {isListening
                    ? t('copilot.listeningHint', 'Suelta...')
                    : isProcessing
                    ? t('copilot.processing', 'Whisper...')
                    : isSpeaking
                    ? 'Hablando...'
                    : t('copilot.voiceAi', 'Voz AI')}
                </span>

                {/* Mute/unmute toggle button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMute();
                  }}
                  className="h-6 w-6 rounded-full border bg-background flex items-center justify-center text-muted-foreground hover:text-foreground shadow-sm transition-colors"
                  title={isMuted ? 'Activar voz hablada' : 'Silenciar voz hablada'}
                >
                  {isMuted ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
                </button>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs mb-2">
            <div className="space-y-1">
              <p className="text-sm font-semibold">
                {isSpeaking
                  ? 'Clic para silenciar respuesta'
                  : isListening
                  ? t('copilot.stopListening', 'Detener escucha')
                  : t('copilot.activateVoice', 'Activar BASS Line AI (Voz)')}
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t('copilot.voiceHint', 'Pregunta "¿cuánto vendí este mes?", "¿qué eventos tengo el sábado?", o di "Nuevo cliente". Atajo:')}{' '}
                <kbd className="px-1 rounded bg-muted border font-mono text-[10px]">Cmd+K</kbd>
              </p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export default VoiceCommandBar;
